import { nullSafeToString, toDisplayPrice } from '../lib/util';

/**
 * The card details recorded against an {@link Order Order}, and what the gateway did with it.
 *
 * Only orders actually paid by card have one of these - terms, PO and zero-total orders have no record
 * at all, and {@link Collaterate.Order.getPaymentInfo Collaterate.Order.getPaymentInfo} resolves with
 * null for them.
 *
 * Nothing here is a full card number: the API stores only the last four digits, and never the CVV or a
 * gateway token that could be replayed.
 *
 * @property {string} cardType - (Read only) The card brand, e.g. "Visa".
 * @property {string} last4CardNumber - (Read only) The last four digits of the card number.
 * @property {string} nameOnCard - (Read only) The cardholder name as entered.
 * @property {string} status - (Read only) The payment's current status.
 * @property {string} email - (Read only) The email recorded with the payment.
 * @property {string} phone - (Read only) The phone recorded with the payment.
 * @property {OrderPaymentInfo~billingAddress} billingAddress - (Read only) The billing address given for the card.
 * @property {OrderPaymentInfo~transaction[]} transactions - (Read only) Every gateway transaction against this card, oldest first. Empty if there are none.
 */

class OrderPaymentInfo {
    constructor (data = {}) {
        this.cardType = nullSafeToString(data.cardType);
        this.last4CardNumber = nullSafeToString(data.last4CardNumber);
        this.nameOnCard = nullSafeToString(data.nameOnCard);
        this.status = nullSafeToString(data.status);
        this.email = nullSafeToString(data.email);
        this.phone = nullSafeToString(data.phone);

        this.billingAddress = Object.freeze({
            company: nullSafeToString(data.company),
            address: nullSafeToString(data.address),
            address2: nullSafeToString(data.address2),
            city: nullSafeToString(data.city),
            state: nullSafeToString(data.state),
            postalCode: nullSafeToString(data.zip),
            country: nullSafeToString(data.country),
        });

        /*
         * An authorization and its later capture are separate rows here, which is how a shopper-facing
         * receipt can tell "held" from "settled" - Checkout.submit only ever authorizes. The admin-only
         * canVoid/canCredit/canPaymentBeCaptured flags and the internal adjustment and log-entry
         * collections on each row are dropped; a storefront can act on none of them.
         */
        this.transactions = Object.freeze((data.transactions || []).map(transaction => Object.freeze({
            id: transaction.id,
            createdOn: transaction.createdOn ? Object.freeze(new Date(transaction.createdOn)) : null,
            action: nullSafeToString(transaction.action),
            amount: transaction.amount || 0,
            displayAmount: toDisplayPrice(transaction.amount),
            isSuccessful: Boolean(transaction.successful),
            gatewayTransactionNumber: nullSafeToString(transaction.gatewayTransactionNum),
        })));

        Object.freeze(this);
    }
}

export default OrderPaymentInfo;

/**
 * The billing address captured with a card payment.
 *
 * @typedef {Object} OrderPaymentInfo~billingAddress
 * @property {string} company - Company on the billing address.
 * @property {string} address - Street address.
 * @property {string} address2 - Second street address line.
 * @property {string} city - City.
 * @property {string} state - State (region).
 * @property {string} postalCode - Postal code.
 * @property {string} country - Country.
 */

/**
 * A single gateway transaction against an order's card.
 *
 * @typedef {Object} OrderPaymentInfo~transaction
 * @property {number} id - The transaction's unique id.
 * @property {?Date} createdOn - When the transaction ran.
 * @property {string} action - What the transaction did, e.g. an authorization or a capture.
 * @property {number} amount - The amount of the transaction.
 * @property {string} displayAmount - amount formatted with commas and two decimal places.
 * @property {boolean} isSuccessful - Whether the gateway accepted it.
 * @property {string} gatewayTransactionNumber - The gateway's own reference for this transaction.
 */
